"use client"

import Link from "next/link"
import Image from "next/image"
import { useLocale } from "next-intl"
import { useTranslations } from "@/lib/intl/use-translations"
import { useCountry } from "@/components/layout/country-provider"
import {
  bookingUrlForCountry,
  PRODUCT_IDS,
  BOOKING_PACKAGE_TYPE,
} from "@/lib/booking"
import { track } from "@/lib/analytics"
import { goKeys as k } from "@/i18n/go-keys"
import { MARKERS } from "./metabolism-data"

export const SECTION = "mx-auto max-w-[1440px] px-6 md:px-10"

/* ── Booking destination for every metabolism CTA. The LP sells the Advanced
   Blood Test, so all CTAs deep-link into the real booking flow
   (booking.aheadhealth.com) with that product preselected and the
   "blood-only" package tab forced — NOT the /pricing page. Mirrors what the
   shared <BookingCta> resolves for the /go/bloodtest LP. ── */
export function useBookingHref(): string {
  const locale = useLocale()
  const { code: country } = useCountry()
  return bookingUrlForCountry(
    country,
    locale,
    PRODUCT_IDS.bloodTest,
    undefined,
    BOOKING_PACKAGE_TYPE.blood
  )
}

/* ── Booking destination for the "full check-up" CTA (blood + full-body MRI):
   the Advanced package, opened on the MRI package tab. ── */
export function useFullCheckupHref(): string {
  const locale = useLocale()
  const { code: country } = useCountry()
  return bookingUrlForCountry(
    country,
    locale,
    PRODUCT_IDS.advanced,
    undefined,
    BOOKING_PACKAGE_TYPE.mri
  )
}

/* ── CTAs for the dark MRI split card (mirrored from the female-hormones LP) ── */
export const CTA_ON_IMAGE =
  "type-title-sm inline-flex w-full items-center justify-center gap-2 rounded-full bg-white px-8 py-3.5 font-medium text-neutral-900 transition-opacity hover:opacity-90 sm:w-auto"
export const CTA_ON_IMAGE_OUTLINE =
  "type-title-sm inline-flex w-full items-center justify-center rounded-full border border-white/40 px-8 py-3.5 font-medium text-white transition-colors hover:bg-white/10 sm:w-auto"

/* ── CTA label with a price suffix that drops on mobile. Splits on the " · "
   separator and hides the trailing price part below `sm` so narrow CTAs read
   "Check your metabolism" (no price) on phones but keep "· from CHF 299" on
   desktop. Labels with no " · " render whole. ── */
export function CtaLabel({ label }: { label: string }) {
  // Match a middot separator (·, •, ∙, ‧) surrounded by whitespace, whichever
  // glyph the copy uses, so the price tail is found regardless of codepoint.
  const m = label.match(/\s[·•∙‧]\s/)
  if (!m || m.index === undefined) return <>{label}</>
  return (
    <>
      {label.slice(0, m.index)}
      <span className="hidden sm:inline">{label.slice(m.index)}</span>
    </>
  )
}

/* ── Risk-reversal reassurance line shown under primary CTAs (not the hero).
   The string is identical on every concern LP in every locale, so it lives on
   the shared `go.ctaReassurance` key rather than a per-page one (AHD-10344).
   14px. `tone` adapts to light vs dark sections. ── */
export function CtaReassurance({
  tone = "onLight",
  className = "",
}: {
  tone?: "onLight" | "onDark"
  className?: string
}) {
  const t = useTranslations("go")
  return (
    <p
      className={`text-sm ${tone === "onDark" ? "text-white/70" : "text-neutral-500"} ${className}`}
    >
      {t(k.ctaReassurance)}
    </p>
  )
}

/* ── Booking CTA link — same destination/visual as a plain <Link>, but fires
   a `cta_click` PostHog event so the ad LP's conversions are tracked. ── */
export function BookingLink({
  href,
  location,
  className,
  children,
}: {
  href: string
  location: string
  className?: string
  children: React.ReactNode
}) {
  return (
    <Link
      href={href}
      className={className}
      onClick={() => track.ctaClick("Metabolism booking", location, href)}
    >
      {children}
    </Link>
  )
}

export function MeasureMarkerCard({
  marker,
  t,
}: {
  marker: (typeof MARKERS)[number]
  t: ReturnType<typeof useTranslations>
}) {
  const Icon = marker.Icon
  return (
    <div className="flex flex-col items-center gap-6 rounded-[24px] bg-neutral-50 px-6 pt-10 pb-8 text-center">
      <span className="relative flex h-36 w-36 items-center justify-center overflow-hidden rounded-[52px] text-white">
        {/* Figma tile gradient behind the organ icon. The grain/noise is a
            separate overlay layer in the design (not baked into these
            gradients) — left to a CSS/effect pass. */}
        <Image
          src={marker.tileImage}
          alt=""
          fill
          sizes="144px"
          className="object-cover"
        />
        <span
          aria-hidden
          className="absolute inset-0 bg-gradient-to-br from-white/25 via-transparent to-black/30"
        />
        {/* organ icons (biomarker-explorer set) are fine fill glyphs; a hairline
            same-colour stroke thickens them a touch */}
        <Icon
          className={`relative z-10 ${marker.iconClassName} [&_path]:[stroke:currentColor] [&_path]:[stroke-width:0.4]`}
        />
      </span>
      {/* reserve 2 lines (top-aligned) + a narrower max width so 1- and 2-line
          titles keep the description on a consistent baseline across cards */}
      <h3 className="type-headline-sm flex min-h-[60px] max-w-[200px] items-start justify-center text-neutral-900">
        {t(marker.titleKey)}
      </h3>
      <p className="type-body-lg text-neutral-600">{t(marker.descKey)}</p>
      <div className="mt-auto flex flex-col items-center gap-4 pt-2">
        {/* marker count merged into the label (was a separate top eyebrow) */}
        <p className="type-label-md text-neutral-400 uppercase">
          {t(marker.eyebrowKey)}
        </p>
        <div className="flex flex-wrap items-center justify-center gap-1.5">
          {marker.chips.map((chip) => (
            <span
              key={chip}
              className="type-label-md rounded-full border border-neutral-200 px-2 py-1 text-neutral-900 normal-case"
            >
              {t(chip)}
            </span>
          ))}
        </div>
      </div>
    </div>
  )
}
